import FuseDialog from '@fuse/core/FuseDialog';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import FuseMessage from '@fuse/core/FuseMessage';
import FuseSuspense from '@fuse/core/FuseSuspense';
import RouteErrorBoundary from 'app/shared-components/RouteErrorBoundary/RouteErrorBoundary';
// import SettingsPanel from 'app/fuse-layouts/shared-components/SettingsPanel';
import { memo, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useRoutes } from 'react-router-dom';
import clsx from 'clsx';

import AppContext from 'app/AppContext';
import HealtheraHeader from 'app/shared-components/Header';
import VideoCallsNotificationPanel from 'app/shared-components/appointment-notification-panel';
import SmsCampaignsBanner from 'app/shared-components/SmsCampaignsBanner';
import IntroPopUps from 'app/fuse-layouts/shared-components/IntroPopUps';
import PaymentOverdueGate from 'app/shared-components/PaymentOverdueGate';
import usePaymentOverdue from 'app/hooks/usePaymentOverdue';

import FooterLayout1 from './components/FooterLayout1';
import LeftSideLayout1 from './components/LeftSideLayout1';
import NavbarWrapperLayout1 from './components/NavbarWrapperLayout1';
import RightSideLayout1 from './components/RightSideLayout1';
import ToolbarLayout1 from './components/ToolbarLayout1';

const Root = styled('div')(({ theme, config }) => ({
  ...(config.mode === 'boxed' && {
    clipPath: 'inset(0)',
    maxWidth: `${config.containerWidth}px`,
    margin: '0 auto',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  }),
  ...(config.mode === 'container' && {
    '& .container': {
      maxWidth: `${config.containerWidth}px`,
      width: '100%',
      margin: '0 auto',
    },
  }),
}));

function Layout1(props) {
  const config = useSelector(({ fuse }) => fuse.settings.current.layout.config);
  const appContext = useContext(AppContext);
  const { routes } = appContext;
  const contentRef = useRef(null);
  const { isBlocked: isPaymentBlocked } = usePaymentOverdue();
  const navbarOpen = useSelector(({ fuse }) => fuse.navbar.open);
  // Pages shown beside the sidebar sit in a card that matches it. Full-screen pages (login, the
  // lite upgrade, the website builder editor) hide the sidebar and keep their own edge-to-edge look.
  const isCard = config.navbar.display;
  // The sidebar card brings its own 6px gutter; without it the page card needs one on the left.
  // The sidebar only sits beside the page from MUI's lg (1200px); below that it's a drawer.
  const hasSidebarGutter = config.navbar.position === 'left' && navbarOpen;

  return (
    <Root
      id="fuse-layout"
      config={config}
      className={clsx('w-full flex', isCard && 'vtwo:bg-[#f5f5f5]')}
    >
      {config.leftSidePanel.display && <LeftSideLayout1 />}

      <div className="flex flex-auto min-w-0">
        {config.navbar.display && config.navbar.position === 'left' && <NavbarWrapperLayout1 />}

        <main
          id="fuse-main"
          className={clsx(
            'flex flex-col flex-auto min-w-0 relative z-10',
            !isCard && 'h-screen',
            isCard &&
              'vtwo:my-1.5 vtwo:mr-1.5 vtwo:h-[calc(100vh-12px)] vtwo:overflow-hidden vtwo:rounded-2xl vtwo:border vtwo:border-[#e8e8e8] vtwo:bg-white',
            isCard && (hasSidebarGutter ? 'vtwo:ml-1.5 vtwo:min-[1200px]:ml-0' : 'vtwo:ml-1.5')
          )}
        >
          {config.healtheraHeader?.options?.pharmacySelector && <VideoCallsNotificationPanel />}

          {config.topBanner?.display && !isPaymentBlocked && <SmsCampaignsBanner />}

          {!isPaymentBlocked && <IntroPopUps />}

          {config.toolbar.display && (
            <ToolbarLayout1 className={config.toolbar.style === 'fixed' && 'sticky top-0'} />
          )}

          {config.healtheraHeader.display && (
            <Box className="h-[56px] shrink-0 grow-0">
              <HealtheraHeader
                className={config.toolbar.style === 'fixed' && 'sticky top-0'}
                options={config.healtheraHeader?.options}
              />
            </Box>
          )}

          {/* <div className="sticky top-0 z-99">
            <SettingsPanel />
          </div> */}

          <div ref={contentRef} className="flex flex-col flex-auto min-h-0 relative z-10">
            <PaymentOverdueGate container={() => contentRef.current} />

            <FuseDialog />

            <FuseSuspense>
              <RouteErrorBoundary>{useRoutes(routes)}</RouteErrorBoundary>
            </FuseSuspense>

            {props.children}
          </div>

          {config.footer.display && (
            <FooterLayout1 className={config.footer.style === 'fixed' && 'sticky bottom-0'} />
          )}
        </main>

        {config.navbar.display && config.navbar.position === 'right' && <NavbarWrapperLayout1 />}
      </div>

      {config.rightSidePanel.display && <RightSideLayout1 />}
      <FuseMessage />
    </Root>
  );
}

export default memo(Layout1);
